import { useEffect, useRef } from 'react';

import { useTrackEvent } from '@liscio/common';

import {
  WHAT_TO_DO_TODAY_COMING_FROM,
  WHAT_TO_DO_TODAY_DASHBOARD_STEPS,
  WHAT_TO_DO_TODAY_DESTINATIONS,
  WHAT_TO_DO_TODAY_EVENTS,
  WHAT_TO_DO_TODAY_SOURCE,
} from './constants';

type WhatToDoTodayDashboardStep =
  (typeof WHAT_TO_DO_TODAY_DASHBOARD_STEPS)[keyof typeof WHAT_TO_DO_TODAY_DASHBOARD_STEPS];
type WhatToDoTodayDestination =
  (typeof WHAT_TO_DO_TODAY_DESTINATIONS)[keyof typeof WHAT_TO_DO_TODAY_DESTINATIONS];

export function hasWhatToDoTodayAttribution(search: string): boolean {
  return new URLSearchParams(search).get('comingFrom') === WHAT_TO_DO_TODAY_COMING_FROM;
}

interface UseTrackWhatToDoTodayDestinationViewParams {
  search: string;
  enabled?: boolean;
  dashboardStep: WhatToDoTodayDashboardStep;
  destination: WhatToDoTodayDestination;
  trackingId: string | number | null | undefined;
  eventProperties: Record<string, string | number>;
}

export function useTrackWhatToDoTodayDestinationView({
  search,
  enabled = true,
  dashboardStep,
  destination,
  trackingId,
  eventProperties,
}: UseTrackWhatToDoTodayDestinationViewParams): void {
  const { trackEvent } = useTrackEvent();
  const firedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || trackingId == null || !hasWhatToDoTodayAttribution(search)) {
      return;
    }

    const trackingKey = `${dashboardStep}:${destination}:${trackingId}`;
    if (firedKeyRef.current === trackingKey) {
      return;
    }

    firedKeyRef.current = trackingKey;
    trackEvent(WHAT_TO_DO_TODAY_EVENTS.destinationViewed, {
      source: WHAT_TO_DO_TODAY_SOURCE,
      dashboard_step: dashboardStep,
      destination,
      ...eventProperties,
    });
  }, [dashboardStep, destination, enabled, eventProperties, search, trackEvent, trackingId]);
}
